import React, { FC } from 'react';
import { format } from 'date-fns';
import TableWithColumnSelectorFixedHeader, { TableType } from '../../core/TableWithColumnSelectorFixedHeader';
import { useQuery } from '@apollo/client';
import { physicianPatientsAnalyticsQuery } from '../../../graph/dashboard';
import { getMinutes } from './util';
import { PhysicianPatientJourneyTableData } from '../../../types/Analytics';
import { DateRangeType } from '../../../types/utils';
import { useScope } from '../../../hooks/useScope';
import { PacuGroupBy } from './PacuGroupBy';

const toTableData = (data: PhysicianPatientJourneyTableData[], hasPostOp?: boolean): TableType => {
  const postOpInOutHeaders = hasPostOp ? ['POSTOP In', 'POSTOP Out'] : [];
  const postOpHeaders = hasPostOp ? ['POSTOP'] : [];
  const baseHeaders = [
    'Date of service',
    'Patient MRN',
    'Patient',
    'OR',
    'Procedure Type',
    'Recovery',
    'Recovery In to Transferred to Chair',
    'Recovery In to Transferred to PT',
    'Recovery In to PT Complete',
    'PT Complete Delay Reason',
    'Transferred to Chair to Transferred to PT',
    'Transferred to PT to Recovery Exit',
    'Transferred to PT to PT Complete',
    'Recovery In to Discharged',
    'Recovery In',
    'Transferred to Chair',
    'Transferred to PT',
    'PT Complete',
    'Recovery Out',
    'Discharged At',
  ];
  return {
    configHeader: [...baseHeaders],
    headers: [
      {
        id: 'h1',
        columns: [...baseHeaders],
      },
    ],
    rows: (data || []).map(e => ({
      id: e?.id.toString(),
      columns: [
        e.surgeryTime,
        e.patientProviderId || '',
        e.patient,
        e.operationRoom,
        e.procedureType,
        getMinutes(e.recoveryInMinutes),
        getMinutes(e.pacuInToTransferToChairInMinutes),
        getMinutes(e.pacuInToTransferToPTInMinutes),
        getMinutes(e.pacuInToPTCompleteInMinutes),
        e.ptCompleteDelayReason || '-',
        getMinutes(e.transferToChairToTransferToPTInMinutes),
        getMinutes(e.transferToPTToPacuOutInMinutes),
        getMinutes(e.transferToPTToPTCompleteInMinutes),
        getMinutes(e.pacuInToDischargedInMinutes),
        e.recoveryIn,
        e.transferToChair,
        e.transferToPT,
        e.ptComplete,
        e.recoveryOut,
        e.dischargedAt,
      ],
    })),
  };
};

const PhysicianPacuPatientsAnalytics: FC<{
  physicianId: number;
  physicianName?: string;
  dateRange: DateRangeType;
  filter: any;
  groupBy?: PacuGroupBy;
  entityLabel?: string;
}> = ({ physicianId, physicianName, dateRange, filter, groupBy, entityLabel }) => {
  const { data } = useQuery(physicianPatientsAnalyticsQuery, {
    variables: {
      physicianId,
      filter,
      groupBy,
    },
  });

  const scope = useScope();
  const hasPostOp = scope?.hospital?.modules?.hasPostop;
  const table: PhysicianPatientJourneyTableData[] = data?.physicianPatientsAnalytics || [];

  return (
    <>
      <TableWithColumnSelectorFixedHeader
        configName="physician-patients-table-config"
        tableId="Physician-Patients-table"
        tableName={`For ${(entityLabel || 'physician').toLowerCase()} ${physicianName || ''}`}
        excelFileName={`Patient-Journey-${format(dateRange.from, 'MM/DD/YYYY')}-${format(dateRange.to, 'MM/DD/YYYY')}`}
        {...toTableData(table, hasPostOp)}
      />
    </>
  );
};

export default PhysicianPacuPatientsAnalytics;
